import { InfoTooltip } from '../Common/InfoTooltip';
import type {
  AtaEntity,
  AtaHealthReport,
  AtaMemoryResponse,
  AtaPredicateProposal,
  AtaRelation,
  AtaSensitiveCategory,
  AtaSensitiveConsentRecord,
  AtaSensitiveConsentResponse,
  AtaTuningResponse,
} from '../../types';

const SENSITIVE_CATEGORY_LABELS: Record<AtaSensitiveCategory, string> = {
  health: 'Gezondheid',
  religion: 'Religie',
  political: 'Politiek',
  sexual_orientation: 'Seksuele oriëntatie',
  adult_content: 'Adult content',
  other_sensitive: 'Overig gevoelig',
};

const SENSITIVE_CATEGORY_TOOLTIPS: Record<AtaSensitiveCategory, string> = {
  health: 'Gezondheidsinformatie, medicatie of ziekte. Alleen opslaan als klant er zelf over begint.',
  religion: 'Geloof, kerk, moskee of andere religieuze voorkeuren.',
  political: 'Politieke voorkeur, partij of verkiezingen.',
  sexual_orientation: 'Geaardheid, gender of relatievoorkeur.',
  adult_content: 'Inhoud voor volwassenen, expliciet of escort-gerelateerd.',
  other_sensitive: 'Overige persoonlijke of privacygevoelige informatie.',
};

const PREDICATE_LABELS: Record<AtaRelation['predicate'], string> = {
  meets_at: 'ontmoet',
  agreed_with: 'is afgesproken met',
  located_at: 'bevindt zich op',
  related_to: 'is gerelateerd aan',
  prefers: 'houdt van',
  dislikes: 'houdt niet van',
};

const confidenceBadge = (confidence: number): { label: string; className: string } => {
  if (confidence > 0.85) return { label: 'High', className: 'ata-conf-high' };
  if (confidence >= 0.65) return { label: 'Medium', className: 'ata-conf-medium' };
  return { label: 'Low', className: 'ata-conf-low' };
};

const statusBadge = (relation: AtaRelation): { label: string; className: string } => {
  if (relation.validUntil === null) return { label: 'geldig', className: 'ata-status-valid' };
  if (relation.invalidationReason === 'user_invalidated')
    return { label: 'ingetrokken: handmatig', className: 'ata-status-user' };
  if (relation.invalidationReason === 'consistency_conflict')
    return { label: 'ingetrokken: consistency', className: 'ata-status-consistency' };
  return { label: 'ingetrokken', className: 'ata-status-user' };
};

const relativeTime = (iso: string): string => {
  const diff = Date.now() - Date.parse(iso);
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'minder dan 1 uur geleden';
  if (hours === 1) return '1 uur geleden';
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 dag geleden';
  return `${days} dagen geleden`;
};

const labelFor = (entityMap: Map<string, AtaEntity>, id: string): string => {
  const entity = entityMap.get(id);
  if (!entity) return id.slice(0, 8);
  const alias = entity.aliases.find((a) => a.length > 0 && a[0] === a[0]!.toUpperCase());
  return alias ?? entity.aliases[0] ?? entity.name;
};

const relationSentence = (entityMap: Map<string, AtaEntity>, relation: AtaRelation): string => {
  const subject = labelFor(entityMap, relation.subjectEntityId);
  const predicate = PREDICATE_LABELS[relation.predicate];
  const object = labelFor(entityMap, relation.objectEntityId);
  return `${subject} ${predicate} ${object}`;
};

type MemoryProps = {
  data: AtaMemoryResponse | null;
  revokingId: string | null;
  onRevoke: (relationId: string) => void;
};

export const AtaMemorySection = ({ data, revokingId, onRevoke }: MemoryProps) => {
  const entityMap = new Map<string, AtaEntity>();
  if (data) for (const e of data.entities) entityMap.set(e.id, e);

  return (
    <section className="ata-card ata-memory-card">
      <div className="ata-section-header">
        <h3>
          Geheugen
          <InfoTooltip text="Feiten die ATA automatisch uit je gesprekken leert. De AI gebruikt deze context om beter te antwoorden." />
        </h3>
        <p className="muted small">
          Beheer feiten die ATA automatisch uit gesprekken leert. Klik op ‘Corrigeer’ als een feit niet klopt.
        </p>
      </div>

      {data && data.relations.length === 0 && (
        <p className="muted">Geen feiten gevonden voor dit account.</p>
      )}

      {data && data.relations.length > 0 && (
        <div className="ata-relations-list">
          {data.relations.map((relation) => {
            const conf = confidenceBadge(relation.confidence);
            const status = statusBadge(relation);
            const sentence = relationSentence(entityMap, relation);
            return (
              <div key={relation.id} className={`ata-relation-row ${status.className}`}>
                <p className="ata-relation-sentence">
                  {sentence}
                  <span className={`ata-conf-badge ${conf.className}`}>{conf.label}</span>
                </p>
                <p className="ata-relation-meta">
                  <span className={`ata-status-badge ${status.className}`}>{status.label}</span>
                  <span className="muted small">
                    {relativeTime(relation.createdAt)} · {relation.sourceContactId}
                  </span>
                  {relation.validUntil === null && (
                    <button
                      type="button"
                      className="button danger xsmall"
                      disabled={revokingId === relation.id}
                      onClick={() => onRevoke(relation.id)}
                    >
                      {revokingId === relation.id ? '…' : 'Corrigeer'}
                    </button>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

type SensitiveProps = {
  consentData: AtaSensitiveConsentResponse | null;
  onToggleDefault: (category: AtaSensitiveCategory, enabled: boolean) => void;
};

export const AtaSensitiveSection = ({ consentData, onToggleDefault }: SensitiveProps) => (
  <section className="ata-card ata-sensitive-card ata-consent-section">
    <div className="ata-section-header ata-consent-header ata-consent-defaults">
      <h3>
        Gevoelige categorieën
        <InfoTooltip text="Bepaal welke gevoelige onderwerpen ATA mag onthouden. Uitgeschakeld = nooit opslaan, ook niet als de klant erover begint." />
      </h3>
      <p className="muted small">
        Account-brede toestemming per categorie. Deze instelling geldt voor alle klanten op dit account.
      </p>
    </div>

    {!consentData && (
      <p className="muted small">Consent-data niet beschikbaar (API-call gefaald).</p>
    )}

    {consentData && (
      <div className="ata-consent-defaults">
        {(Object.entries(consentData.defaults.categoryEnabledByDefault) as [AtaSensitiveCategory, boolean | 'not_configured'][]).map(([cat, val]) => (
          <div key={cat} className="ata-toggle-row">
            <span className="ata-toggle-text">
              {SENSITIVE_CATEGORY_LABELS[cat]}
              <InfoTooltip text={SENSITIVE_CATEGORY_TOOLTIPS[cat]} />
            </span>
            <label className="ata-toggle-label switch">
              <span className="sr-only">{SENSITIVE_CATEGORY_LABELS[cat]}</span>
              <input
                type="checkbox"
                checked={val === true}
                onChange={(e) => onToggleDefault(cat, e.target.checked)}
              />
              <span aria-hidden="true" />
            </label>
          </div>
        ))}
      </div>
    )}

    {consentData && consentData.consents.length > 0 && (
      <div className="ata-consent-list">
        <strong>Per-klant consent:</strong>
        <ul className="ata-health-list">
          {consentData.consents.slice(0, 20).map((c: AtaSensitiveConsentRecord) => (
            <li key={c.id}>
              {c.contactId} — {c.category}: <strong>{c.status}</strong>
              {' '}({c.source})
            </li>
          ))}
        </ul>
      </div>
    )}
  </section>
);

type PlatformOwnerProps = {
  healthReports: AtaHealthReport[];
  proposals: AtaPredicateProposal[];
  tuningData: AtaTuningResponse | null;
  actioningId: string | null;
  onApproveProposal: (id: string) => void;
  onRejectProposal: (id: string) => void;
  onRollbackTuning: (id: string) => void;
};

export const AtaPlatformOwnerSection = ({
  healthReports,
  proposals,
  tuningData,
  actioningId,
  onApproveProposal,
  onRejectProposal,
  onRollbackTuning,
}: PlatformOwnerProps) => (
  <div className="ata-platform-grid">
    <section className="ata-card ata-health-section">
      <div className="ata-section-header">
        <h3>
          Dagrapport
          <InfoTooltip text="Technisch overzicht voor de platformeigenaar. ATA signaleert eigen zwakke plekken en inconsistenties in de afgelopen 24 uur." />
        </h3>
        <p className="muted small">
          ATA signaleert zwakke plekken in de afgelopen 24 uur. Puur signalerend — past niets automatisch aan.
        </p>
      </div>
      {healthReports.length === 0 && (
        <p className="muted small">Nog geen dagrapporten beschikbaar. Het eerste rapport wordt automatisch gegenereerd binnen 24 uur.</p>
      )}
      {healthReports.map((report) => (
        <div key={report.id} className="ata-health-report">
          <p className="ata-health-summary">{report.summary}</p>
          <p className="muted small">
            {relativeTime(report.generatedAt)} · periode {report.periodStart.slice(0, 10)}–{report.periodEnd.slice(0, 10)}
          </p>

          {report.conflictingEntities.length > 0 && (
            <div className="ata-health-subsection">
              <strong>Tegenstrijdige entiteiten</strong>
              <ul className="ata-health-list">
                {report.conflictingEntities.map((entry) => (
                  <li key={entry.entityId}>
                    {entry.entityName} — {entry.conflictCount} conflicten
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.uncertainContacts.length > 0 && (
            <div className="ata-health-subsection">
              <strong>Onzekere contacten</strong>
              <ul className="ata-health-list">
                {report.uncertainContacts.map((entry) => (
                  <li key={entry.contactId}>
                    {entry.contactId} — {entry.unresolvedOutcomeCount} onopgelost
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.predicateDistribution.length > 0 && (
            <div className="ata-health-subsection">
              <strong>Predicate-verdeling</strong>
              <ul className="ata-health-list">
                {report.predicateDistribution.map((entry) => (
                  <li key={entry.predicate}>
                    {entry.predicate} — {entry.count}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </section>

    <section className="ata-card ata-proposals-section">
      <div className="ata-section-header">
        <h3>
          Predicate-voorstellen
          <InfoTooltip text="Nieuwe soorten feiten die ATA wil leren. Alleen platformeigenaars kunnen deze goedkeuren." />
        </h3>
        <p className="muted small">
          ATA stelt nieuwe predicates voor na ≥3 signalen in 24 uur. Goedkeuring is de enige plek voor schema-uitbreiding.
        </p>
      </div>
      {proposals.length === 0 && (
        <p className="muted small">Nog geen voorstellen. ATA verzamelt signalen en stelt automatisch voorstellen voor bij ≥3 gevallen in 24 uur.</p>
      )}
      {proposals.map((proposal) => (
        <div key={proposal.id} className="ata-proposal-card">
          <p><strong>{proposal.predicateName}</strong> — {proposal.occurrences} signalen</p>
          {proposal.isSensitive && (
            <p className="muted small">⚠️ Gevoelig: {proposal.sensitiveCategory}</p>
          )}
          <p className="muted small">Voorbeelden: {proposal.exampleTexts.slice(0, 3).join('; ')}</p>
          <div className="ata-proposal-actions">
            <button
              className="button small"
              disabled={actioningId === proposal.id}
              onClick={() => onApproveProposal(proposal.id)}
            >
              Goedkeuren
            </button>
            <button
              className="button small danger"
              disabled={actioningId === proposal.id}
              onClick={() => onRejectProposal(proposal.id)}
            >
              Afwijzen
            </button>
          </div>
        </div>
      ))}
    </section>

    <section className="ata-card ata-tuning-section">
      <div className="ata-section-header">
        <h3>
          Tuning-log
          <InfoTooltip text="Automatische ATA-instellingaanpassingen (Tier 2). Elke wijziging is hier terug te draaien." />
        </h3>
        <p className="muted small">
          ATA past autonoom drempels en promptomvang aan. Elke wijziging is terug te draaien.
        </p>
      </div>
      {!tuningData && (
        <p className="muted small">Tuning-data niet beschikbaar (API-call gefaald).</p>
      )}
      {tuningData && (
        <>
          <div className="ata-tuning-settings">
            <strong>Huidige instellingen:</strong>
            <ul className="ata-health-list">
              <li>maxRelationsInPrompt: {tuningData.settings.maxRelationsInPrompt}</li>
              <li>correctionConfidenceBoost: {tuningData.settings.correctionConfidenceBoost}</li>
              <li>decayRatePerDay: {tuningData.settings.decayRatePerDay}</li>
              {Object.entries(tuningData.settings.confidenceThresholds).map(([pred, thresh]) => (
                <li key={pred}>drempel {pred}: {thresh}</li>
              ))}
            </ul>
          </div>
          {tuningData.logs.length > 0 && (
            <div className="ata-tuning-logs">
              <strong>Wijzigingsgeschiedenis:</strong>
              <ul className="ata-health-list">
                {tuningData.logs.slice(0, 20).map((log) => (
                  <li key={log.id}>
                    {log.parameter}: {log.oldValue} → {log.newValue} ({log.reason})
                    {log.supersededBy && <em className="muted"> (overschreven)</em>}
                    {' '}
                    <button
                      className="button danger xsmall"
                      disabled={actioningId === log.id}
                      onClick={() => onRollbackTuning(log.id)}
                    >
                      ↩ rollback
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  </div>
);
